import React, { useEffect, useState } from "react";
import { Modal, Button, Checkbox } from "antd";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { PDFDocument } from "pdf-lib";

interface PreviewFile {
  url: string;
  name: string;
  showUpload: boolean;
  documentType: string;
}

interface FilePreviewModalProps {
  isVisible: boolean;
  onClose: () => void;
  files: PreviewFile[];
  handleUpload: (files: { documentType: string; file: File }[]) => void;
  showUploadButton: boolean;
  key?: any;
  isLoading?: boolean;
}

const MultipleSelectedFilesPreviewModal: React.FC<FilePreviewModalProps> = ({
  isVisible,
  onClose,
  files = [],
  handleUpload,
  showUploadButton,
  isLoading,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);

  // Store page counts and selections keyed by file index
  const [filePagesCount, setFilePagesCount] = useState<Record<number, number>>(
    {}
  );
  const [fileSelectedPages, setFileSelectedPages] = useState<
    Record<number, number[]>
  >({});
  const [fileSelectAll, setFileSelectAll] = useState<Record<number, boolean>>(
    {}
  );

  const currentFile = files[currentIndex] || {
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  };
  const isPdf = currentFile.name.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
    if (isPdf) {
      loadPdfPages(currentFile.url, currentIndex);
    } else {
      setFilePagesCount((prev) => ({ ...prev, [currentIndex]: 1 }));
      setFileSelectedPages((prev) => ({ ...prev, [currentIndex]: [1] }));
      setFileSelectAll((prev) => ({ ...prev, [currentIndex]: true }));
    }
  }, [currentIndex, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setCurrentIndex(0);
      setFilePagesCount({});
      setFileSelectedPages({});
      setFileSelectAll({});
    }
  }, [isVisible]);

  // Load PDF pages count for a file url and index
  const loadPdfPages = async (url: string, fileIndex: number) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const totalPages = pdfDoc.getPageCount();

      setFilePagesCount((prev) => ({ ...prev, [fileIndex]: totalPages }));
      setFileSelectedPages((prev) => ({
        ...prev,
        [fileIndex]: Array.from({ length: totalPages }, (_, i) => i + 1),
      }));
      setFileSelectAll((prev) => ({ ...prev, [fileIndex]: true }));
    } catch (error) {
      console.error("Error loading PDF pages:", error);
      setFilePagesCount((prev) => ({ ...prev, [fileIndex]: 0 }));
      setFileSelectedPages((prev) => ({ ...prev, [fileIndex]: [] }));
      setFileSelectAll((prev) => ({ ...prev, [fileIndex]: false }));
    }
  };

  const handlePageSelection = (pageNum: number) => {
    setFileSelectedPages((prev) => {
      const currentSelected = prev[currentIndex] || [];
      const newSelected = currentSelected.includes(pageNum)
        ? currentSelected.filter((p) => p !== pageNum)
        : [...currentSelected, pageNum].sort((a, b) => a - b);

      setFileSelectAll((selectAllPrev) => ({
        ...selectAllPrev,
        [currentIndex]:
          newSelected.length === (filePagesCount[currentIndex] || 0),
      }));

      return { ...prev, [currentIndex]: newSelected };
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setFileSelectAll((prev) => ({ ...prev, [currentIndex]: checked }));
    setFileSelectedPages((prev) => ({
      ...prev,
      [currentIndex]: checked
        ? Array.from(
            { length: filePagesCount[currentIndex] || 0 },
            (_, i) => i + 1
          )
        : [],
    }));
  };

  // Extract selected pages from PDF and return a new File
  const extractPagesFromFile = async (
    file: PreviewFile,
    selectedPages: number[]
  ) => {
    const response = await fetch(file.url);
    const arrayBuffer = await response.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const newPdfDoc = await PDFDocument.create();

    const pagesToCopy = selectedPages.map((p) => p - 1);
    const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToCopy);
    copiedPages.forEach((page) => newPdfDoc.addPage(page));

    const pdfBytes: any = await newPdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    return new File([blob], file.name, { type: "application/pdf" });
  };

  // Extract files for all selected files and upload as array
  const extractAndUploadAllFiles = async () => {
    try {
      const filesToUpload: { documentType: string; file: File }[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isPdf = file.name.toLowerCase().endsWith(".pdf");
        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|tiff)$/i.test(file.name);
        const selectedPages = fileSelectedPages[i] || [];

        if (isPdf) {
          if (selectedPages.length === 0) continue;
          const slicedFile = await extractPagesFromFile(file, selectedPages);
          filesToUpload.push({
            documentType: file.documentType,
            file: slicedFile,
          });
        } else if (isImage) {
          // If it’s an image, fetch and convert it to a File
          const response = await fetch(file.url);
          const blob = await response.blob();
          const imgFile = new File([blob], file.name, { type: blob.type });

          filesToUpload.push({
            documentType: file.documentType,
            file: imgFile,
          });
        }
      }

      if (filesToUpload.length === 0) {
        console.warn("No files or pages selected to upload.");
        return;
      }

      // Upload all files as an array in the required structure
      handleUpload(filesToUpload);
    } catch (error) {
      console.error("Error extracting and uploading files:", error);
    }
  };

  const handlePrev = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
  const handleNext = () =>
    setCurrentIndex((prev) => Math.min(prev + 1, files.length - 1));
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  return (
    <div className="preview-modal">
      <Modal
        title={
          files.length > 1
            ? `Preview (${currentIndex + 1}/${files.length}): ${
                currentFile.name
              }`
            : `Preview: ${currentFile.name}`
        }
        open={isVisible}
        onCancel={onClose}
        footer={[
          showUploadButton && (
            <Button
              key="upload"
              type="primary"
              onClick={extractAndUploadAllFiles}
              loading={isLoading}
              style={{ marginTop: "0.6rem" }}
              disabled={
                files.length === 0 ||
                Object.values(fileSelectedPages).every(
                  (pages) => pages.length === 0
                )
              }
            >
              Upload Selected Files
            </Button>
          ),
          <Button key="close" onClick={onClose} style={{ marginTop: "0.8rem" }}>
            Close
          </Button>,
        ].filter(Boolean)}
        width={700}
        bodyStyle={{ height: "120vh" }}
        wrapClassName="preview-modal"
      >
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {files.length > 1 && (
            <Button
              icon={<LeftOutlined />}
              onClick={handlePrev}
              disabled={currentIndex === 0}
              style={{ marginRight: 8 }}
            />
          )}

          <div style={{ flex: 1, position: "relative", height: "100%" }}>
            {isPdf && filePagesCount[currentIndex] > 1 && (
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "10px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Checkbox
                  checked={fileSelectAll[currentIndex] || false}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  style={{ fontWeight: "bold", marginBottom: 8 }}
                >
                  Select All Pages ({filePagesCount[currentIndex]})
                </Checkbox>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {Array.from(
                    { length: filePagesCount[currentIndex] },
                    (_, i) => i + 1
                  ).map((pageNum) => (
                    <Checkbox
                      key={pageNum}
                      checked={
                        fileSelectedPages[currentIndex]?.includes(pageNum) ||
                        false
                      }
                      onChange={() => handlePageSelection(pageNum)}
                    >
                      Page {pageNum}
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}

            {isPdf ? (
              <object
                data={currentFile.url}
                type="application/pdf"
                width="100%"
                height="90%"
                style={{
                  transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                }}
              >
                <embed
                  src={currentFile.url}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                />
                <p>
                  This browser does not support PDFs. Please download the PDF to
                  view it:{" "}
                  <a href={currentFile.url} download={currentFile.name}>
                    Download PDF
                  </a>
                  .
                </p>
              </object>
            ) : (
              <>
                <div
                  style={{
                    overflow: "auto",
                    width: "100%",
                    height: "120vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    marginTop: 20,
                  }}
                >
                  <div
                    style={{
                      minWidth: `${100 * zoomLevel}%`,
                      minHeight: `${100 * zoomLevel}%`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={currentFile.url}
                      alt={currentFile.name}
                      style={{
                        transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                        transition: "transform 0.3s ease",
                        maxWidth: "none",
                        maxHeight: "none",
                        width: "100%",
                        height: "auto",
                      }}
                    />
                  </div>
                </div>

                <div className="zoom-controls" style={{ marginTop: "0.3rem" }}>
                  <Button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    style={{ marginRight: "0.5rem" }}
                  >
                    <ZoomOutOutlined />
                  </Button>
                  <Button onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                    <ZoomInOutlined />
                  </Button>
                  <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                    <RotateLeftOutlined />
                  </Button>
                  <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                    <RotateRightOutlined />
                  </Button>
                </div>
              </>
            )}
          </div>

          {files.length > 1 && (
            <Button
              icon={<RightOutlined />}
              onClick={handleNext}
              disabled={currentIndex === files.length - 1}
              style={{ marginLeft: 8 }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MultipleSelectedFilesPreviewModal;
